/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React from 'react';
import { Text, View } from 'react-native';
import { styles } from '../ReportsScreen.styles';
import { spacing } from '../../../shared/theme/spacing';
import { DATE_RANGE_PRESET_LABELS, DATE_RANGE_PRESETS, type DateRangePreset } from '../reportsScreen.constants';
import { InventoryIsoDateField } from '../../../shared/ui/InventoryIsoDateField';
import { formatIsoDate, parseIsoDate } from '@shared/vueltoDateTime';
import { OperationalFilterChipGroup, OperationalFilterChipScrollRow, OperationalFilterFieldLabel } from '../../../shared/filters/OperationalFilterPrimitives'
import { useOperationalFilterLayout } from '../../../shared/filters/useOperationalFilterLayout';

type Props = {
  dateRangePreset: DateRangePreset;
  setDateRangePreset: (p: DateRangePreset) => void;
  customRangeFrom: Date;
  setCustomRangeFrom: (d: Date) => void;
  customRangeTo: Date;
  setCustomRangeTo: (d: Date) => void;
  disabled?: boolean;
  presetAccessibilityPrefix: string;
};

export function ReportsDateRangePicker({
  dateRangePreset,
  setDateRangePreset,
  customRangeFrom,
  setCustomRangeFrom,
  customRangeTo,
  setCustomRangeTo,
  disabled = false,
  presetAccessibilityPrefix,
}: Props) {
  const { layoutWide } = useOperationalFilterLayout();

  const presetOptions = DATE_RANGE_PRESETS.map((preset) => ({
    value: preset,
    label: DATE_RANGE_PRESET_LABELS[preset],
    accessibilityLabel: `${presetAccessibilityPrefix} ${DATE_RANGE_PRESET_LABELS[preset]}`,
  }));

  const onPresetChange = (p: DateRangePreset) => {
    if (!disabled) setDateRangePreset(p);
  };

  const presetControls = layoutWide ? (
    <OperationalFilterChipGroup options={presetOptions} value={dateRangePreset} onChange={onPresetChange} />
  ) : (
    <OperationalFilterChipScrollRow options={presetOptions} value={dateRangePreset} onChange={onPresetChange} />
  );

  return (
    <>
      {presetControls}
      {dateRangePreset === 'custom' ? (
        <View
          style={{
            gap: layoutWide ? spacing.sm : spacing.xs,
            marginTop: layoutWide ? spacing.sm : spacing.xs,
            pointerEvents: disabled ? 'none' : 'auto',
          }}
        >
          {layoutWide ? <Text style={styles.caption}>Elegí desde y hasta (inclusive).</Text> : null}
          <View style={{ gap: spacing.xs }}>
            <OperationalFilterFieldLabel>Desde</OperationalFilterFieldLabel>
            <InventoryIsoDateField
              value={formatIsoDate(customRangeFrom)}
              onChange={(ymd) => {
                const d = parseIsoDate(ymd);
                d.setHours(0, 0, 0, 0);
                setCustomRangeFrom(d);
                // BUG-17: si from > to, auto-ajustar to al final del día de from
                if (d > customRangeTo) {
                  const adjusted = new Date(d);
                  adjusted.setHours(23, 59, 59, 999);
                  setCustomRangeTo(adjusted);
                }
              }}
              emptyLabel="Elegí fecha"
              ariaLabel="Fecha desde"
              showClearButton={false}
              iosEmptyBehavior="openPicker"
            />
          </View>
          <View style={{ gap: spacing.xs }}>
            <OperationalFilterFieldLabel>Hasta</OperationalFilterFieldLabel>
            <InventoryIsoDateField
              value={formatIsoDate(customRangeTo)}
              onChange={(ymd) => {
                const d = parseIsoDate(ymd);
                d.setHours(23, 59, 59, 999);
                setCustomRangeTo(d);
              }}
              emptyLabel="Elegí fecha"
              ariaLabel="Fecha hasta"
              showClearButton={false}
              iosEmptyBehavior="openPicker"
            />
          </View>
        </View>
      ) : null}
    </>
  );
}
